import React, { useState } from 'react';
import { User, X, Twitter, Linkedin, Sparkles, FileText } from 'lucide-react';
import { ExpositorPublico } from '../types/congreso.types';

interface SpeakersSectionProps {
  expositores: ExpositorPublico[];
}

export const SpeakersSection: React.FC<SpeakersSectionProps> = ({ expositores }) => {
  const [selectedSpeaker, setSelectedSpeaker] = useState<ExpositorPublico | null>(null);

  return (
    <section id="disertantes" className="py-24 bg-white relative border-t border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-50 border border-brand-cyan/30 text-brand-navy text-xs font-bold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5 text-brand-cyan" />
            Especialistas & Referentes
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-brand-navy tracking-tight">
            Panel de Disertantes
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            Líderes de opinión, juristas, economistas y expertos en proptech debatiendo el presente y futuro inmobiliario.
          </p>
        </div>

        {expositores.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {expositores.map((speaker) => (
              <div
                key={speaker.idExpositor}
                onClick={() => setSelectedSpeaker(speaker)}
                className="group bg-white rounded-3xl p-6 flex flex-col items-center text-center cursor-pointer relative overflow-hidden border border-slate-200/90 shadow-sm hover:shadow-xl hover:border-brand-cyan/50 transition-all duration-300"
              >
                {/* Halo decorativo sutil de fondo */}
                <div className="absolute -top-12 -right-12 w-28 h-28 bg-brand-cyan/5 rounded-full blur-2xl group-hover:bg-brand-cyan/15 transition-all pointer-events-none"></div>

                {/* Foto con aro oficial corporativo */}
                <div className="relative mb-6">
                  <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-brand-navy via-brand-cyan to-sky-400 shadow-md group-hover:scale-105 transition-transform duration-300">
                    {speaker.fotoUrl ? (
                      <img
                        src={speaker.fotoUrl}
                        alt={speaker.nombreCompleto}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-slate-100 flex items-center justify-center text-brand-navy">
                        <User className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Nombre y Cargo */}
                <h3 className="text-xl font-bold text-brand-navy group-hover:text-brand-cyan transition-colors mb-1">
                  {speaker.nombreCompleto}
                </h3>
                <p className="text-xs font-semibold text-brand-cyan uppercase tracking-wider mb-2">
                  {speaker.cargoInstitucion && speaker.cargoInstitucion !== speaker.bio ? speaker.cargoInstitucion : 'Disertante'}
                </p>

                {/* Preview de Bio */}
                {speaker.bio && speaker.bio !== speaker.cargoInstitucion && (
                  <p className="text-xs text-slate-500 line-clamp-3 mb-4 leading-relaxed">
                    {speaker.bio}
                  </p>
                )}

                {/* CTA para abrir perfil completo en modal */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSpeaker(speaker);
                  }}
                  className="mt-auto inline-flex items-center gap-1 text-xs font-bold text-brand-navy group-hover:text-brand-cyan transition-colors"
                >
                  Ver Perfil Completo &rarr;
                </button>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State (FR-10) */
          <div className="bg-slate-50 rounded-3xl p-12 text-center max-w-2xl mx-auto flex flex-col items-center border border-slate-200">
            <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center border border-slate-200 text-brand-cyan mb-6 shadow-sm">
              <User className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold text-brand-navy mb-3">
              Panel de Disertantes en Confirmación
            </h3>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6">
              Próximamente anunciaremos los disertantes y especialistas que formarán parte de esta edición del congreso.
            </p>
            <div className="inline-flex items-center gap-2 text-xs text-brand-navy font-semibold bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm">
              <Sparkles className="w-4 h-4 text-brand-cyan" />
              Nuevos anuncios cada semana
            </div>
          </div>
        )}

        {/* Modal de Perfil de Expositor */}
        {selectedSpeaker && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl max-w-lg w-full p-8 relative shadow-2xl border border-slate-200">
              <button
                onClick={() => setSelectedSpeaker(null)}
                className="absolute top-6 right-6 p-2 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200 transition-colors"
                aria-label="Cerrar modal"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex flex-col items-center text-center">
                <div className="w-28 h-28 rounded-full p-1 bg-gradient-to-tr from-brand-navy to-brand-cyan mb-4 shadow-md">
                  {selectedSpeaker.fotoUrl ? (
                    <img
                      src={selectedSpeaker.fotoUrl}
                      alt={selectedSpeaker.nombreCompleto}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-slate-100 flex items-center justify-center text-brand-navy">
                      <User className="w-10 h-10" />
                    </div>
                  )}
                </div>

                <h3 className="text-2xl font-extrabold text-brand-navy mb-1">
                  {selectedSpeaker.nombreCompleto}
                </h3>
                <p className="text-sm font-semibold text-brand-cyan mb-4">
                  {selectedSpeaker.cargoInstitucion && selectedSpeaker.cargoInstitucion !== selectedSpeaker.bio ? selectedSpeaker.cargoInstitucion : 'Disertante'}
                </p>

                <div className="text-left text-sm text-slate-700 leading-relaxed mb-6 bg-slate-50 p-4 rounded-2xl border border-slate-200 max-h-60 overflow-y-auto">
                  {selectedSpeaker.bio || 'Sin biografía adicional disponible.'}
                </div>

                {/* Botón de descarga Perfil Profesional (PDF) - Opcional */}
                {Boolean(selectedSpeaker.documentoPerfilUrl && selectedSpeaker.documentoPerfilUrl.trim()) && (
                  <div className="w-full mb-6">
                    <a
                      href={selectedSpeaker.documentoPerfilUrl!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-sky-50 hover:bg-sky-100 text-brand-navy border border-sky-200 hover:border-brand-cyan/50 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm"
                      data-testid="btn-descargar-perfil-pdf"
                    >
                      <FileText className="w-4 h-4 text-brand-cyan" />
                      Descargar Perfil Profesional (PDF)
                    </a>
                  </div>
                )}

                {/* Redes */}
                <div className="flex items-center gap-4">
                  {selectedSpeaker.twitterUrl && (
                    <a
                      href={selectedSpeaker.twitterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2.5 rounded-full bg-slate-100 hover:bg-brand-navy text-slate-700 hover:text-white transition-colors"
                      aria-label="Twitter"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {selectedSpeaker.linkedInUrl && (
                    <a
                      href={selectedSpeaker.linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2.5 rounded-full bg-slate-100 hover:bg-brand-navy text-slate-700 hover:text-white transition-colors"
                      aria-label="LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
